import React, { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'

import { usePoolCreateMutation } from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Input } from '../../../../../common/uikit/Input'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { RootState } from '../../../../redux/store'

const formId = 'create-pool-form'

type CreatePoolDialogProps = {
    visible: boolean
    onSuccess: () => void
    onHide: () => void
}

type Inputs = {
    name: string
}

export const CreatePoolDialog = memo(function CreatePoolDialog({
    visible,
    onSuccess,
    onHide,
}: CreatePoolDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const [createPool, { isLoading }] = usePoolCreateMutation()
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitted, isSubmitting, isValid },
    } = useForm<Inputs>({
        defaultValues: { name: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        if (visible) reset()
    }, [reset, visible])

    const onSubmit = async ({ name }: Inputs) => {
        try {
            await createPool({ pool: { name, companyId } }).unwrap()
            toast.notifySuccess(text('msgPoolAdded'))
            onSuccess()
        } catch {
            toast.notifyError(text('msgPoolNotCreated'))
        }
    }

    const submitDisabled = isLoading || isSubmitting || (isSubmitted && !isValid)
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button
                label={text('btnCreate')}
                type="submit"
                form={formId}
                disabled={submitDisabled}
                loading={isLoading || isSubmitting}
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblNewPoolModal')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="create-pool-dialog"
        >
            <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
                <Input
                    id="pool-name"
                    label={text('lblName')}
                    {...register('name', { required: text('msgNameRequired') })}
                    error={errors.name?.message}
                    required
                    autoComplete="off"
                />
            </form>
        </Dialog>
    )
})
